import { Injectable } from '@angular/core';
import { Event, NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject } from 'rxjs';
import {
	IFRAME_EMBED_MAIN_CONTENT_TOP_PULL_UP_PX,
	IFRAME_EMBED_TOOLBAR_CHROME_PX,
} from '../config/iframe-embed-layout.config';
import { distinctUntilChanged, filter } from 'rxjs/operators';

/** Same key as AppUI2 iframe embed so a parent can align behavior later. */
const IFRAME_MODE_STORAGE_KEY = 'iframeMode';

/**
 * When the URL query contains `iframeMode=true`, Command Center hides left nav and uses the iframe header
 * shell. Sticky via sessionStorage until `iframeMode=false` or tab close.
 *
 * Test: `http://localhost:4200/home?iframeMode=true` (after login, or add param on any navigated URL once).
 */
@Injectable()
export class IframeEmbedService {
  /** Single light toolbar row height (px); matches `cc-embed-toolbar` min-height. */
  readonly embedToolbarChromePx = IFRAME_EMBED_TOOLBAR_CHROME_PX;
  /**
   * Fallback when `nav.iframeEmbedHeaderStackPx` is not set yet (0): trim a few px off the sum so `top` does not
   * overshoot; prefer measured stack from `IframeEmbedHeaderComponent`.
   */
  readonly iframeMainContentTopPullUpPx = IFRAME_EMBED_MAIN_CONTENT_TOP_PULL_UP_PX;

  private readonly enabled = new BehaviorSubject<boolean>(this.readStored());

  constructor(private readonly router: Router) {
    this.applyFromWindowSearch();
    this.router.events
      .pipe(filter((e: Event): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe((e) => this.applyFromRouterUrl(e.urlAfterRedirects));
    this.enabled.pipe(distinctUntilChanged()).subscribe(() => this.syncBodyClass());
  }

  get isIframeEmbed(): boolean {
    return this.enabled.value;
  }

  get iframeEmbed$() {
    return this.enabled.asObservable();
  }

  private readStored(): boolean {
    if (typeof sessionStorage === 'undefined') {
      return false;
    }
    return sessionStorage.getItem(IFRAME_MODE_STORAGE_KEY) === 'true';
  }

  private applyFromWindowSearch(): void {
    if (typeof window === 'undefined' || typeof window.location === 'undefined') {
      return;
    }
    const q = window.location.search;
    const v = new URLSearchParams(q.startsWith('?') ? q.slice(1) : q).get('iframeMode');
    this.applyIframeModeParam(v);
  }

  private applyFromRouterUrl(url: string): void {
    if (!url) {
      return;
    }
    const tree = this.router.parseUrl(url);
    const v = tree.queryParams['iframeMode'];
    this.applyIframeModeParam(v === undefined ? null : String(v));
  }

  private applyIframeModeParam(value: string | null): void {
    if (typeof sessionStorage === 'undefined') {
      return;
    }
    if (value === 'true') {
      sessionStorage.setItem(IFRAME_MODE_STORAGE_KEY, 'true');
      this.enabled.next(true);
    } else if (value === 'false') {
      sessionStorage.removeItem(IFRAME_MODE_STORAGE_KEY);
      this.enabled.next(false);
    }
  }

  /** Portaled modals (e.g. Text Message Template) are under body, not app-master — iframe SCSS uses this class. */
  private syncBodyClass(): void {
    if (typeof document === 'undefined' || !document.body) {
      return;
    }
    if (this.enabled.value) {
      document.body.classList.add('cc-iframe-embed-active');
    } else {
      document.body.classList.remove('cc-iframe-embed-active');
    }
  }
}
